import {Component, OnDestroy, OnInit} from '@angular/core';
import {OrderService} from '../../../services/order.service';
import {PaymentService} from '../../../services/payment.service';
import {AdminOrder} from '../../../classes/AdminOrder';
import {Subscription} from 'rxjs';
import {environment} from '../../../../environments/environment';
import {HttpClient, HttpResponse} from '@angular/common/http';
import { saveAs } from 'file-saver';
import {LocalStorageService} from '../../../services/local-storage.service';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {LanguageService} from '../../../services/language.service';
import {OrderStatus} from '../../../enums/OrderStatus';
import {NgbCalendar, NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';
import {Page} from '../../../classes/Page';

@Component({
  selector: 'app-admin-order-list',
  styleUrls: ['./admin-order-list.component.scss'],
  templateUrl: './admin-order-list.component.html',
})
export class AdminOrderListComponent implements OnInit {

  public orders: AdminOrder[];
  public isSuperAdmin = false;
  public filterForm: FormGroup;
  public orderStatuses = OrderStatus;
  public orderStatusKeys: string[];
  public errorMessage;
  public pageNumber = 1;
  public totalElements = 0;
  public pageSizes = [10, 20, 50];
  public pageSize = this.pageSizes[0];

  constructor(private orderService: OrderService,
              private paymentService: PaymentService,
              private httpClient: HttpClient,
              private localStorageService: LocalStorageService,
              private formBuilder: FormBuilder,
              private calendar: NgbCalendar,
              public languageService: LanguageService) {}

  public ngOnInit(): void {

    this.orderStatusKeys = Object.keys(OrderStatus);

    const user = this.localStorageService.getUser();
    this.isSuperAdmin = user.roles.includes('ROLE_SUPER_ADMIN');

    const today = new Date();
    const startDate = this.convertDate(new Date(Date.UTC(today.getFullYear(), today.getMonth(), 1, 0, 0, 0)));
    const endDate = this.calendar.getToday();

    this.filterForm = this.formBuilder.group({
      startDate: [startDate, Validators.required],
      endDate: [endDate, Validators.required],
      orderStatus: ['ALL']
    });

    this.filterForm.controls.startDate.valueChanges.subscribe(() => this.refreshList());
    this.filterForm.controls.endDate.valueChanges.subscribe(() => this.refreshList());
    this.filterForm.controls.orderStatus.valueChanges.subscribe(() => this.refreshList());

    this.refreshList();
  }

  private refreshList(): void {

    const startDate = this.convertNgbDate(this.filterForm.controls.startDate.value, 0, 0, 0);
    const endDate = this.convertNgbDate(this.filterForm.controls.endDate.value, 23, 59, 59);
    let orderStatus = this.filterForm.controls.orderStatus.value;

    if (orderStatus === 'ALL') {
      orderStatus = null;
    }

    this.orderService.getAllOrders(startDate, endDate, orderStatus, this.pageNumber - 1, this.pageSize)
      .subscribe({
        next: (response: Page<AdminOrder>) => {
          this.orders = response.content;
          this.totalElements = response.totalElements;
        }
      });
  }

  public async orderCheckout(orderId: number, duration: number, orderType: string): Promise<void> {
    return this.paymentService.orderCheckout(orderId, duration, orderType);
  }

  public orderCancel(id: number): void {
    this.orderService.cancelOrder(id).subscribe(
      () => this.refreshList(),
    );
  }

  public async invoice(id): Promise<void> {
    return this.paymentService.invoice(id);
  }

  generateReport(): Subscription {
    return this.httpClient.get(
      `${environment.backendApiUrl}/v1/admin/orders/report`,
      {observe: 'response', responseType: 'blob' as 'json'}
    ).subscribe((response: HttpResponse<Blob>) => {
      const filename = response.headers.get('content-disposition')
        .split(';')[1]
        .split('filename')[1]
        .split('=')[1]
        .trim();
      filename.replace('"', ''); // to remove the first " from filename
      filename.replace('"', ''); // to remove the last " from filename
      saveAs(response.body, filename);
    });
  }

  // TODO move it to service, because it also used in book.component.ts
  convertDate(date: Date): NgbDateStruct {
    return date ? {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate()
    } : null;
  }

  // TODO move it to service, because it also used in book.component.ts
  convertNgbDate(ngbDate: NgbDateStruct, hours: number, minutes: number, seconds: number): Date {

    const date = new Date(ngbDate.year, ngbDate.month - 1, ngbDate.day, hours, minutes, seconds);

    if (hours === 0) {
      date.setDate( date.getDate() );
    }

    return ngbDate ? date : null;
  }

  sendToMonday(order): void {
    this.orderService.sendToMonday(order.id).subscribe({
      next: () => this.refreshList(),
      error: (error) => this.errorMessage = error.error.error
    });
  }

  handlePageChange(event): void {
    this.pageNumber = event;
    this.refreshList();
  }

  handlePageSizeChange(event): void {
    this.pageSize = event.target.value;
    this.pageNumber = 1;
    this.refreshList();
  }

  getMinLimit(): number {
    return this.pageSize * (this.pageNumber - 1) + 1;
  }

  getMaxLimit(): number {
    const maxLimit = this.pageSize * this.pageNumber;
    return maxLimit <= this.totalElements ? maxLimit : this.totalElements;
  }
}
